/* eslint-disable prettier/prettier */
import { Dispatch, Fragment, SetStateAction } from "react";
import { Box, Button, Card, Chip, Link, TableCell, TableRow } from "@mui/material";
import { formatInTimeZone } from "date-fns-tz";

import { SortIcon, Table } from "@APP/components";
import { tableStyles as useStyles } from "@APP/styles";
import { AnonymisationStatus, SortType, User, UserSearchResponse, UserSortBy } from "@APP/types";
import { SCREEN_PATHS } from "@APP/navigation";
import { commonFormatDate } from "@APP/constants";
import { getUserTimeZone } from "@APP/utils";
import toCamelCase from "@APP/utils/toCamelCase";
import { useAlert } from "@APP/hooks";

interface HeadCell {
  id: UserSortBy;
  rightAlign: boolean;
  disablePadding: boolean;
  label: string;
}

interface UserProps {
  data: UserSearchResponse[];
  entries: number;
  page: number;
  lastPage: number;
  setPage?: (page: number) => void;
  handleOnEntriesChange: (entries: number) => void;
  order: "asc" | "desc";
  setOrder: Dispatch<SetStateAction<"asc" | "desc">>;
  orderBy: string;
  setOrderBy: Dispatch<SetStateAction<string>>;
  onAnonymize: (orgId: string, customerId: string) => void;
}

const HEADER_CELL_ITEMS: HeadCell[] = [
  { id: UserSortBy.username, rightAlign: false, disablePadding: false, label: "Username" },
  { id: UserSortBy.status, rightAlign: false, disablePadding: false, label: "Status" },
  { id: UserSortBy.orgName, rightAlign: true, disablePadding: false, label: "Organization name" },
  {
    id: UserSortBy.createdAt,
    rightAlign: false,
    disablePadding: false,
    label: "Created Date",
  },
  {
    id: UserSortBy.createdAtTime,
    rightAlign: false,
    disablePadding: false,
    label: "Created Time",
  },
];

const EMPTY_ROW_COL_SPAN = HEADER_CELL_ITEMS.length + 3;

const UserList = ({
  data,
  entries,
  page,
  lastPage,
  setPage,
  handleOnEntriesChange,
  order,
  setOrder,
  orderBy,
  setOrderBy,
  onAnonymize,
}: UserProps) => {
  const classes = useStyles();
  const alert = useAlert();

  const getAnonymisationStatusChip = (status?: AnonymisationStatus) => {
    if (!status) return null;

    const statusConfig: Record<
      Exclude<AnonymisationStatus, null>,
      {
        label: string;
        color: "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning";
      }
    > = {
      Pending: { label: "Pending", color: "info" },
      InProgress: { label: "In Progress", color: "warning" },
      Completed: { label: "Completed", color: "success" },
      Successful: { label: "Successful", color: "success" },
      Failed: { label: "Failed", color: "error" },
    };

    const config = statusConfig[status];

    return <Chip label={config.label} color={config.color} size="small" />;
  };

  const handleAnonymizeClick = (orgId: string, customerId: string, username: string) => {
    alert.open(
      "Confirm Anonymization",
      `Are you sure you want to anonymize user "${username}"? This action will start the anonymization process.`,
      [
        {
          text: "Cancel",
          onClick: () => alert.close(),
        },
        {
          text: "OK",
          onClick: () => {
            alert.close();
            onAnonymize(orgId, customerId);
          },
        },
      ],
    );
  };

  const handleRequestSort = (property: string) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const renderHeader = () => (
    <TableRow>
      {HEADER_CELL_ITEMS.map(({ id, label, disablePadding }) => (
        <TableCell
          key={id}
          align="left"
          padding={disablePadding ? "none" : "normal"}
          sortDirection={order}
          onClick={() => (data?.length ? handleRequestSort(toCamelCase(label)) : null)}
          style={{ cursor: data?.length ? "pointer" : "default" }}>
          <Box display="flex" alignItems="center" data-testid={`${label}-field-label`}>
            {label}
            {!!data?.length && (
              <SortIcon active={orderBy === toCamelCase(label)} type={order as SortType} />
            )}
          </Box>
        </TableCell>
      ))}
      <TableCell align="left">Anonymisation Status</TableCell>
      <TableCell align="left">Actions</TableCell>
    </TableRow>
  );

  const renderRows = ({
    id,
    username,
    status,
    orgName,
    orgId,
    customerId,
    anonymisationsStatus,
    meta: { createdAt },
  }: User) => {
    // Check if user has required IDs
    const hasRequiredIds = !!(customerId && orgId);

    // User can be anonymized if status is null, undefined, or "Failed"
    // Cannot anonymize if "Pending", "InProgress", "Completed", or "Successful"
    const isAnonymizable = !anonymisationsStatus || anonymisationsStatus === "Failed";

    // Button is enabled only if user is anonymizable AND has required IDs
    const isButtonDisabled = !hasRequiredIds || !isAnonymizable;

    return (
      <Fragment key={id}>
        <TableRow className={classes.tableRow}>
          <TableCell>
            <Link href={`${SCREEN_PATHS.UPDATE_USER}/${username}`}>{username}</Link>
          </TableCell>
          <TableCell>{status}</TableCell>
          <TableCell>
            <Link href={`${SCREEN_PATHS.ORG_DETAILS}/${orgId}`}>{orgName || " "}</Link>
          </TableCell>
          <TableCell>
            {createdAt ? formatInTimeZone(createdAt, getUserTimeZone(), commonFormatDate) : "-"}
          </TableCell>
          <TableCell>
            {createdAt ? formatInTimeZone(createdAt, getUserTimeZone(), "HH:mm") : "-"}
          </TableCell>
          <TableCell>
            {createdAt ? formatInTimeZone(createdAt, getUserTimeZone(), "HH:mm") : "-"}
          </TableCell>
          <TableCell>{getAnonymisationStatusChip(anonymisationsStatus)}</TableCell>
          <TableCell>
            <Button
              variant="outlined"
              color="primary"
              size="small"
              disabled={isButtonDisabled}
              onClick={() => hasRequiredIds && handleAnonymizeClick(orgId, customerId, username)}
              data-testid={`anonymize-btn-${username}`}>
              Anonymize
            </Button>
          </TableCell>
        </TableRow>
      </Fragment>
    );
  };

  const renderEmptyDataRow = () => (
    <TableRow>
      <TableCell colSpan={EMPTY_ROW_COL_SPAN} align="center">
        No Matching Users found.
      </TableCell>
    </TableRow>
  );

  return (
    <Box my={4}>
      <Card elevation={12}>
        <Table
          renderHeader={renderHeader}
          data={data || []}
          showPagination={true}
          renderRows={renderRows}
          onEntriesChange={handleOnEntriesChange}
          onPageChange={setPage}
          entries={entries}
          page={page}
          lastPage={lastPage}
          renderEmptyDataRow={renderEmptyDataRow}
        />
      </Card>
    </Box>
  );
};

export default UserList;
